import { useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';

import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { fromWorkflowTemplate, toWorkflowTemplateBody } from '../api/mappers';
import { WorkflowTemplateForm } from '../components/workflow-template-form';
import { useWorkflowTemplate } from '../hooks/use-workflow-template';
import {
  useCreateWorkflowTemplate,
  useUpdateWorkflowTemplate,
} from '../hooks/use-workflow-template-mutations';
import { emptyStage, type WorkflowFormInput } from '../schemas/workflow-form-schema';

function emptyTemplate(): WorkflowFormInput {
  return { name: '', stages: [emptyStage()] };
}

function WorkflowTemplateCreate() {
  const { t } = useTranslation('workflows');
  const store = useStore();
  const navigate = useNavigate();
  const create = useCreateWorkflowTemplate(store.id);

  return (
    <PageContent>
      <PageHeader
        title={t('editor.createTitle')}
        backTo={{ to: '/s/$storeId/settings/workflows', params: { storeId: store.id } }}
        backLabel={t('title')}
      />
      <WorkflowTemplateForm
        storeId={store.id}
        defaultValues={emptyTemplate()}
        expandInitial
        isSubmitting={create.isPending}
        submitLabel={t('editor.create')}
        onSubmit={async (values) => {
          const created = await create.mutateAsync(toWorkflowTemplateBody(values));
          await navigate({
            to: '/s/$storeId/settings/workflows/$templateId',
            params: { storeId: store.id, templateId: created.id },
            replace: true,
          });
        }}
      />
    </PageContent>
  );
}

function WorkflowTemplateEdit() {
  const { t } = useTranslation('workflows');
  const format = useFormat();
  const store = useStore();
  const { editable, reason } = useStoreEditable({ permission: 'settings.write' });
  // Not strict so the page also renders under a test router; the route always has the param.
  const { templateId = '' } = useParams({ strict: false });
  // Prefetched by the route loader, so this resolves from cache.
  const { data: template } = useWorkflowTemplate(store.id, templateId);
  const update = useUpdateWorkflowTemplate(store.id);
  if (!template) return null;

  return (
    <PageContent>
      <PageHeader
        title={template.name}
        meta={
          template.isDefault ? <Badge variant="secondary">{t('editor.defaultBadge')}</Badge> : null
        }
        description={
          <span title={format.utc(template.updatedAt)}>
            {t('stageCount', { count: template.stages.length })}
            {' · '}
            {t('updatedAt', { when: format.relative(template.updatedAt) })}
          </span>
        }
        backTo={{ to: '/s/$storeId/settings/workflows', params: { storeId: store.id } }}
        backLabel={t('title')}
      />
      <ReadOnlyBanner reason={reason} />
      <WorkflowTemplateForm
        key={template.updatedAt}
        storeId={store.id}
        defaultValues={fromWorkflowTemplate(template)}
        readOnly={!editable}
        isSubmitting={update.isPending}
        submitLabel={t('editor.save')}
        onSubmit={(values) =>
          update.mutateAsync({ id: template.id, body: toWorkflowTemplateBody(values) })
        }
        aside={
          <FormSection title={t('editor.stagesTitle')}>
            <p className="text-muted-foreground text-sm">{t('editor.runningNote')}</p>
            {template.isDefault ? null : (
              <p className="text-muted-foreground text-sm">{t('editor.makeDefaultHint')}</p>
            )}
          </FormSection>
        }
      />
    </PageContent>
  );
}

export function WorkflowTemplateCreatePage() {
  return (
    <FeatureErrorBoundary name="workflows.create">
      <WorkflowTemplateCreate />
    </FeatureErrorBoundary>
  );
}

export function WorkflowTemplateEditPage() {
  return (
    <FeatureErrorBoundary name="workflows.edit">
      <WorkflowTemplateEdit />
    </FeatureErrorBoundary>
  );
}
